import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { FormEvent, useState } from "react";
import { Alert, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useInvalidateArborApplications, useArborApplications, arborApplicationsQueryKey } from "../../hooks/useArborApplications";
import IArborApplication, { IArborApplicationCreateRequest, IArborApplicationCredentialUpdateRequest, IArborApplicationMetadataUpdateRequest } from "../../models/ArborApplication";
import Layout from "../layout";
import MISButton from "../../components/button";
import MISTooltip from "../../components/tooltip";
import { ServiceManager } from "../../services/ServiceManager";

interface ICreateForm {
    applicationName: string;
    arborID: string;
    tokenID: string;
    arborUsername: string;
    token: string;
    dateIssued: string;
    dateExpiry: string;
}

interface IPatchForm {
    token: string;
    dateExpiry: string;
}

const initialCreateForm: ICreateForm = {
    applicationName: "",
    arborID: "",
    tokenID: "",
    arborUsername: "",
    token: "",
    dateIssued: "",
    dateExpiry: ""
};

const initialPatchForm: IPatchForm = {
    token: "",
    dateExpiry: ""
};

function getErrorStatus(error: unknown): number | undefined {
    if (typeof error !== "object" || error === null) {
        return undefined;
    }

    const errorDetails = error as { response?: unknown; status?: unknown };
    if (typeof errorDetails.response === "object" && errorDetails.response !== null) {
        const responseStatus: unknown = (errorDetails.response as { status?: unknown }).status;
        if (typeof responseStatus === "number") {
            return responseStatus;
        }
    }

    return typeof errorDetails.status === "number" ? errorDetails.status : undefined;
}

function getSafeErrorMessage(error: unknown, fallback: string): string {
    const status: number | undefined = getErrorStatus(error);

    if (status === 401) {
        return "Your session has expired. Please sign in again.";
    }

    if (status === 403) {
        return "You do not have permission to perform this action.";
    }

    if (status === 404) {
        return "The selected application is no longer available.";
    }

    if (status === 409) {
        return "The application changed or is still in use. Refresh and try again.";
    }

    return fallback;
}

function isValidToken(token: string): boolean {
    return token.startsWith("dpt_");
}

function getCreateValidationMessage(form: ICreateForm): string | null {
    const errors: string[] = [];
    const issuedDate: Date = new Date(form.dateIssued);
    const expiryDate: Date = new Date(form.dateExpiry);

    if (!form.applicationName.trim()) {
        errors.push("Application name is required.");
    }

    if (Number(form.arborID) <= 0) {
        errors.push("Arbor ID must be a positive number.");
    }

    if (Number(form.tokenID) <= 0) {
        errors.push("Token ID must be a positive number.");
    }

    if (!form.arborUsername.trim()) {
        errors.push("Arbor username is required.");
    }

    if (!isValidToken(form.token)) {
        errors.push("Token must start with dpt_.");
    }

    if (!form.dateIssued) {
        errors.push("Issued date is required.");
    }

    if (!form.dateExpiry) {
        errors.push("Expiry date is required.");
    }

    if (form.dateIssued && form.dateExpiry && issuedDate >= expiryDate) {
        errors.push("Expiry date must be after the issued date.");
    }

    if (form.dateExpiry && expiryDate <= new Date()) {
        errors.push("Expiry date must be in the future.");
    }

    return errors.length > 0 ? `Correct the following: ${errors.join(" ")}` : null;
}

function getPatchValidationMessage(form: IPatchForm): string | null {
    const errors: string[] = [];

    if (!isValidToken(form.token)) {
        errors.push("A replacement token is required and must start with dpt_.");
    }

    if (!form.dateExpiry) {
        errors.push("Expiry date is required.");
    }
    else if (new Date(form.dateExpiry) <= new Date()) {
        errors.push("Expiry date must be in the future.");
    }

    return errors.length > 0 ? `Correct the following: ${errors.join(" ")}` : null;
}

function getMetadataValidationMessage(form: IArborApplicationMetadataUpdateRequest): string | null {
    const errors: string[] = [];

    if (!form.applicationName?.trim()) {
        errors.push("Application name is required.");
    }

    if (!form.arborUsername?.trim()) {
        errors.push("Arbor username is required.");
    }

    return errors.length > 0 ? `Correct the following: ${errors.join(" ")}` : null;
}

export default function ArborKeyManagement() {
    const queryClient = useQueryClient();
    const invalidateApplications = useInvalidateArborApplications();
    const { data: applications = [], isLoading, isError, refetch } = useArborApplications();
    const [selectedApplication, setSelectedApplication] = useState<IArborApplication | null>(null);
    const [createForm, setCreateForm] = useState<ICreateForm>(initialCreateForm);
    const [metadataForm, setMetadataForm] = useState<IArborApplicationMetadataUpdateRequest>({});
    const [patchForm, setPatchForm] = useState<IPatchForm>(initialPatchForm);
    const [showCreate, setShowCreate] = useState<boolean>(false);
    const [showManage, setShowManage] = useState<boolean>(false);
    const [showPatch, setShowPatch] = useState<boolean>(false);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [createMessage, setCreateMessage] = useState<string>("");
    const [metadataMessage, setMetadataMessage] = useState<string>("");
    const [patchMessage, setPatchMessage] = useState<string>("");
    const [message, setMessage] = useState<string>("");

    const createMutation = useMutation({
        mutationFn: (request: IArborApplicationCreateRequest) => ServiceManager.ArborApplicationService.CreateApplication(request),
        onSuccess: async () => {
            setCreateForm(initialCreateForm);
            setCreateMessage("");
            setShowCreate(false);
            ServiceManager.ToastService.showSuccess("Arbor application created.");
            await invalidateApplications();
        },
        onError: (error) => {
            setCreateForm((current) => ({ ...current, token: "" }));
            setCreateMessage(getSafeErrorMessage(error, "Application creation failed. Re-enter the token before trying again."));
        }
    });

    const metadataMutation = useMutation({
        mutationFn: ({ applicationID, request }: { applicationID: string; request: IArborApplicationMetadataUpdateRequest }) =>
            ServiceManager.ArborApplicationService.UpdateApplicationMetadata(applicationID as IArborApplication["applicationID"], request),
        onSuccess: async () => {
            setShowManage(false);
            ServiceManager.ToastService.showSuccess("Arbor application metadata updated.");
            await invalidateApplications();
        },
        onError: (error) => setMetadataMessage(getSafeErrorMessage(error, "Application metadata update failed."))
    });

    const patchMutation = useMutation({
        mutationFn: ({ applicationID, request }: { applicationID: string; request: IArborApplicationCredentialUpdateRequest }) =>
            ServiceManager.ArborApplicationService.UpdateApplicationCredential(applicationID as IArborApplication["applicationID"], request),
        onSuccess: async () => {
            setPatchForm(initialPatchForm);
            setPatchMessage("");
            setShowPatch(false);
            ServiceManager.ToastService.showSuccess("Arbor application updated with new token.");
            await invalidateApplications();
        },
        onError: (error) => {
            setPatchForm((current) => ({ ...current, token: "" }));
            setPatchMessage(getSafeErrorMessage(error, "Token change failed. Re-enter the token before trying again."));
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (applicationID: IArborApplication["applicationID"]) => ServiceManager.ArborApplicationService.DeleteApplication(applicationID),
        onSuccess: async () => {
            setSelectedApplication(null);
            setShowDelete(false);
            setMessage("Application removed from MIS Central active use.");
            await queryClient.invalidateQueries({ queryKey: arborApplicationsQueryKey });
        },
        onError: (error) => setMessage(getSafeErrorMessage(error, "Application removal failed."))
    });

    function selectApplication(application: IArborApplication): void {
        setSelectedApplication(application);
        setMetadataForm({ applicationName: application.applicationName, arborUsername: application.arborUsername });
        setMetadataMessage("");
        setPatchForm({ token: "", dateExpiry: application.dateExpiry });
        setMessage("");
        setShowManage(true);
    }

    function openDeleteDialog(application: IArborApplication): void {
        setSelectedApplication(application);
        setMessage("");
        setShowDelete(true);
    }

    function submitCreate(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        const validationMessage: string | null = getCreateValidationMessage(createForm);
        if (validationMessage) {
            setCreateMessage(validationMessage);
            return;
        }

        createMutation.mutate({
            applicationName: createForm.applicationName.trim(),
            arborID: Number(createForm.arborID),
            tokenID: Number(createForm.tokenID),
            arborUsername: createForm.arborUsername.trim(),
            token: createForm.token,
            dateIssued: new Date(createForm.dateIssued).toISOString(),
            dateExpiry: new Date(createForm.dateExpiry).toISOString()
        });
    }

    function submitMetadata(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        const validationMessage: string | null = getMetadataValidationMessage(metadataForm);
        if (!selectedApplication || validationMessage) {
            setMetadataMessage(validationMessage ?? "The selected application is no longer available.");
            return;
        }

        metadataMutation.mutate({
            applicationID: selectedApplication.applicationID,
            request: {
                applicationName: metadataForm.applicationName?.trim(),
                arborUsername: metadataForm.arborUsername?.trim()
            }
        });
    }

    function submitPatch(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        const validationMessage: string | null = getPatchValidationMessage(patchForm);
        if (!selectedApplication || validationMessage) {
            setPatchMessage(validationMessage ?? "The selected application is no longer available.");
            return;
        }

        patchMutation.mutate({
            applicationID: selectedApplication.applicationID,
            request: {
                token: patchForm.token || undefined,
                dateExpiry: patchForm.dateExpiry ? new Date(patchForm.dateExpiry).toISOString() : undefined
            }
        });
    }

    return (
        <Layout
            title="Arbor Key Management"
            button={<MISButton text="Create application" className="btn btn-primary crbc-bg-color" data-testid="arbor-management-create-button" type="button" FnOnClick={() => { setCreateForm(initialCreateForm); setCreateMessage(""); setShowCreate(true); setMessage(""); }} />}
        >
            {message && <Alert variant="info" role="status" data-testid="arbor-management-message">{message}</Alert>}
            {isLoading && <div className="d-flex justify-content-center" role="status"><Spinner animation="border" variant="info" /></div>}
            {isError && <Alert variant="danger">Unable to load Arbor applications. <button className="btn btn-link p-0" type="button" data-testid="arbor-management-retry-button" onClick={() => refetch()}>Retry</button></Alert>}
            {!isLoading && !isError && applications.length === 0 && <Alert variant="secondary">No Arbor applications are available.</Alert>}
            {!isLoading && !isError && applications.length > 0 && (
                <Row>
                    <Col xs={12}>
                        <Table responsive borderless className="arbor-applications-table" data-testid="arbor-applications-table">
                            <thead><tr><th>Application</th><th>Arbor ID</th><th>Token ID</th><th>Expiry</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
                            <tbody>
                                {applications.map((application) => (
                                    <tr key={application.applicationID}>
                                        <td>{application.applicationName}</td>
                                        <td>{application.arborID}</td>
                                        <td>{application.tokenID}</td>
                                        <td>{new Date(application.dateExpiry).toLocaleDateString()}</td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-end">
                                                <MISTooltip message="Manage application" placement="top">
                                                    <button type="button" className="btn icon-btn" aria-label={`Manage ${application.applicationName}`} data-testid={`arbor-management-edit-${application.applicationID}`} onClick={() => selectApplication(application)}>
                                                        <FontAwesomeIcon icon={faPen} />
                                                    </button>
                                                </MISTooltip>
                                                <MISTooltip message="Delete application" placement="top">
                                                    <button type="button" className="btn icon-btn" aria-label={`Delete ${application.applicationName}`} data-testid={`arbor-management-delete-${application.applicationID}`} onClick={() => openDeleteDialog(application)}>
                                                        <FontAwesomeIcon icon={faTrashCan} />
                                                    </button>
                                                </MISTooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            )}

            <Modal show={showManage} onHide={() => setShowManage(false)}>
                <Modal.Header closeButton><Modal.Title>Manage Arbor application</Modal.Title></Modal.Header>
                {selectedApplication && <Form onSubmit={submitMetadata} data-testid="arbor-management-metadata-form"><Modal.Body>
                    {metadataMessage && <Alert id="arbor-management-metadata-message" variant="danger" role="alert" data-testid="arbor-management-metadata-message">{metadataMessage}</Alert>}
                    <h5>{selectedApplication.applicationName}</h5>
                    <Row className="mb-3">
                        <Col md={6}><span className="d-block text-muted">Arbor ID</span>{selectedApplication.arborID}</Col>
                        <Col md={6}><span className="d-block text-muted">Token ID</span>{selectedApplication.tokenID}</Col>
                    </Row>
                    <Form.Group className="mb-2"><Form.Label htmlFor="arbor-management-name">Application name</Form.Label><Form.Control id="arbor-management-name" data-testid="arbor-management-name-input" aria-describedby={metadataMessage ? "arbor-management-metadata-message" : undefined} aria-invalid={Boolean(metadataMessage)} value={metadataForm.applicationName ?? ""} maxLength={100} onChange={(event) => setMetadataForm((current) => ({ ...current, applicationName: event.target.value }))} /></Form.Group>
                    <Form.Group><Form.Label htmlFor="arbor-management-username">Arbor username</Form.Label><Form.Control id="arbor-management-username" data-testid="arbor-management-username-input" aria-describedby={metadataMessage ? "arbor-management-metadata-message" : undefined} aria-invalid={Boolean(metadataMessage)} value={metadataForm.arborUsername ?? ""} maxLength={100} onChange={(event) => setMetadataForm((current) => ({ ...current, arborUsername: event.target.value }))} /></Form.Group>
                </Modal.Body><Modal.Footer className="d-flex flex-nowrap gap-2 align-items-stretch" data-testid="arbor-management-manage-actions">
                    <MISButton text="Cancel" className="btn btn-outline-secondary flex-fill d-flex align-items-center justify-content-center" data-testid="arbor-management-manage-cancel-button" type="button" FnOnClick={() => setShowManage(false)} />
                    <MISButton text="Issue token" className="btn btn-warning flex-fill d-flex align-items-center justify-content-center" data-testid="arbor-management-open-patch-button" type="button" FnOnClick={() => { setPatchForm({ token: "", dateExpiry: selectedApplication.dateExpiry.slice(0, 10) }); setPatchMessage(""); setShowManage(false); setShowPatch(true); }} />
                    <MISButton text="Save metadata" className="btn btn-primary crbc-bg-color flex-fill d-flex align-items-center justify-content-center" data-testid="arbor-management-save-metadata-button" type="submit" disabled={metadataMutation.isPending} FnOnClick={() => undefined} />
                </Modal.Footer></Form>}
            </Modal>

            <Modal show={showCreate} onHide={() => { setCreateForm((current) => ({ ...current, token: "" })); setCreateMessage(""); setShowCreate(false); }}>
                <Modal.Header closeButton><Modal.Title>Create Arbor application</Modal.Title></Modal.Header>
                <Form onSubmit={submitCreate} data-testid="arbor-management-create-form"><Modal.Body>
                    {createMessage && <Alert id="arbor-management-create-message" variant="danger" role="alert" data-testid="arbor-management-create-message">{createMessage}</Alert>}
                    <Form.Group className="mb-2"><Form.Label htmlFor="arbor-management-create-name">Application name</Form.Label><Form.Control id="arbor-management-create-name" data-testid="arbor-management-create-name-input" aria-describedby={createMessage ? "arbor-management-create-message" : undefined} aria-invalid={Boolean(createMessage)} value={createForm.applicationName} maxLength={100} required onChange={(event) => setCreateForm((current) => ({ ...current, applicationName: event.target.value }))} /></Form.Group>
                    <Row className="mb-2">
                        <Col md={6}><Form.Group><Form.Label htmlFor="arbor-management-create-arbor-id">Arbor ID</Form.Label><Form.Control id="arbor-management-create-arbor-id" data-testid="arbor-management-create-arbor-id-input" aria-describedby={createMessage ? "arbor-management-create-message" : undefined} aria-invalid={Boolean(createMessage)} type="number" min={1} required value={createForm.arborID} onChange={(event) => setCreateForm((current) => ({ ...current, arborID: event.target.value }))} /></Form.Group></Col>
                        <Col md={6}><Form.Group><Form.Label htmlFor="arbor-management-create-token-id">Token ID</Form.Label><Form.Control id="arbor-management-create-token-id" data-testid="arbor-management-create-token-id-input" aria-describedby={createMessage ? "arbor-management-create-message" : undefined} aria-invalid={Boolean(createMessage)} type="number" min={1} required value={createForm.tokenID} onChange={(event) => setCreateForm((current) => ({ ...current, tokenID: event.target.value }))} /></Form.Group></Col>
                    </Row>
                    <Form.Group className="mb-2"><Form.Label htmlFor="arbor-management-create-username">Arbor username</Form.Label><Form.Control id="arbor-management-create-username" data-testid="arbor-management-create-username-input" aria-describedby={createMessage ? "arbor-management-create-message" : undefined} aria-invalid={Boolean(createMessage)} maxLength={100} required value={createForm.arborUsername} onChange={(event) => setCreateForm((current) => ({ ...current, arborUsername: event.target.value }))} /></Form.Group>
                    <Form.Group className="mb-2"><Form.Label htmlFor="arbor-management-create-token">Token</Form.Label><Form.Control id="arbor-management-create-token" data-testid="arbor-management-create-token-input" aria-describedby={createMessage ? "arbor-management-create-message" : undefined} aria-invalid={Boolean(createMessage)} type="password" required value={createForm.token} onChange={(event) => setCreateForm((current) => ({ ...current, token: event.target.value }))} /></Form.Group>
                    <Row>
                        <Col md={6}><Form.Group><Form.Label htmlFor="arbor-management-create-issued">Issued</Form.Label><Form.Control id="arbor-management-create-issued" data-testid="arbor-management-create-issued-input" aria-describedby={createMessage ? "arbor-management-create-message" : undefined} aria-invalid={Boolean(createMessage)} type="date" required value={createForm.dateIssued} onChange={(event) => setCreateForm((current) => ({ ...current, dateIssued: event.target.value }))} /></Form.Group></Col>
                        <Col md={6}><Form.Group><Form.Label htmlFor="arbor-management-create-expiry">Expiry</Form.Label><Form.Control id="arbor-management-create-expiry" data-testid="arbor-management-create-expiry-input" aria-describedby={createMessage ? "arbor-management-create-message" : undefined} aria-invalid={Boolean(createMessage)} type="date" required value={createForm.dateExpiry} onChange={(event) => setCreateForm((current) => ({ ...current, dateExpiry: event.target.value }))} /></Form.Group></Col>
                    </Row>
                </Modal.Body><Modal.Footer><MISButton text="Cancel" className="btn btn-outline-secondary" data-testid="arbor-management-create-cancel-button" type="button" FnOnClick={() => { setCreateForm((current) => ({ ...current, token: "" })); setCreateMessage(""); setShowCreate(false); }} /><MISButton text="Create" className="btn btn-primary" data-testid="arbor-management-create-submit-button" type="submit" disabled={createMutation.isPending} FnOnClick={() => undefined} /></Modal.Footer></Form>
            </Modal>

            <Modal show={showPatch} onHide={() => { setPatchForm((current) => ({ ...current, token: "" })); setPatchMessage(""); setShowPatch(false); }}>
                <Modal.Header closeButton><Modal.Title>Issue token</Modal.Title></Modal.Header>
                <Form onSubmit={submitPatch} data-testid="arbor-management-patch-form"><Modal.Body>
                    {patchMessage && <Alert id="arbor-management-patch-message" variant="danger" role="alert" data-testid="arbor-management-patch-message">{patchMessage}</Alert>}
                    <Form.Group className="mb-2"><Form.Label htmlFor="arbor-management-patch-token">Replacement token</Form.Label><Form.Control id="arbor-management-patch-token" data-testid="arbor-management-patch-token-input" aria-describedby={patchMessage ? "arbor-management-patch-message" : undefined} aria-invalid={Boolean(patchMessage)} type="password" aria-required="true" value={patchForm.token} onChange={(event) => setPatchForm((current) => ({ ...current, token: event.target.value }))} /></Form.Group>
                    <Form.Group><Form.Label htmlFor="arbor-management-patch-expiry">Expiry</Form.Label><Form.Control id="arbor-management-patch-expiry" data-testid="arbor-management-patch-expiry-input" aria-describedby={patchMessage ? "arbor-management-patch-message" : undefined} aria-invalid={Boolean(patchMessage)} type="date" aria-required="true" value={patchForm.dateExpiry} onChange={(event) => setPatchForm((current) => ({ ...current, dateExpiry: event.target.value }))} /></Form.Group>
                </Modal.Body><Modal.Footer><MISButton text="Cancel" className="btn btn-outline-secondary" data-testid="arbor-management-patch-cancel-button" type="button" FnOnClick={() => { setPatchForm((current) => ({ ...current, token: "" })); setPatchMessage(""); setShowPatch(false); }} /><MISButton text="Issue token" className="btn btn-primary" data-testid="arbor-management-patch-submit-button" type="submit" disabled={patchMutation.isPending} FnOnClick={() => undefined} /></Modal.Footer></Form>
            </Modal>

            <Modal show={showDelete} onHide={() => setShowDelete(false)}>
                <Modal.Header closeButton><Modal.Title>Delete Arbor application</Modal.Title></Modal.Header>
                <Modal.Body>Remove {selectedApplication?.applicationName} from active MIS Central use?</Modal.Body>
                <Modal.Footer><MISButton text="Cancel" className="btn btn-outline-secondary" data-testid="arbor-management-delete-cancel-button" type="button" FnOnClick={() => setShowDelete(false)} /><MISButton text="Delete" className="btn btn-danger" data-testid="arbor-management-delete-confirm-button" type="button" disabled={deleteMutation.isPending} FnOnClick={() => selectedApplication && deleteMutation.mutate(selectedApplication.applicationID)} /></Modal.Footer>
            </Modal>
        </Layout>
    );
}